// Espacio de una cotización: encabezado (folio, cliente, contado de la recomendada), avisos rojos clavados arriba,
// barra de secciones y la sección abierta. Cada sección es una ruta (/cotizaciones/:id/<seccion>): atrás funciona.
import { pdfDesactualizado } from '../../logica/pdf-al-dia.ts';
import { useEffect, useRef, useState } from 'react';
import { ArrowDown, ArrowLeft, FileDown, Lock, OctagonAlert, Pencil, RefreshCw, RotateCcw, Trash2 } from 'lucide-react';
import { hoyEnHermosillo, promoAlDia, CODIGOS_QUE_APRUEBA_DIEGO, CODIGOS_QUE_NO_SE_CONFIRMAN, CODIGOS_QUE_SOLO_CONFIRMA_DIEGO, MENSAJE_EN_PAPELERA, RECIBOS_MAXIMOS_POR_MEDIDOR, fechaHoraCorta, formatoPara, lecturaPorAutorizar, loQueCobraElPdf, margenPiezas, numero, recibosEsperados } from '@cotizador/compartido';
import type { Aviso, ContenidoCotizacion, Cotizacion, Medidor, NivelRespaldoCalculado, Semaforo } from '@cotizador/compartido';
import { api } from '../../api/consultas.ts';
import { ErrorDeApi } from '../../api/cliente.ts';
import { Enlace, navegar, tomarLlegadaALaSeccion } from '../../ruteo.tsx';
import { mostrarLaSeccion } from '../../logica/llegada.ts';
import { usarUsuario } from '../../sesion.tsx';
import { BandaAvisos, detieneElPdf } from '../../componentes/avisos.tsx';
import { BandaActualizarVersion, BandaRojoDeOtraVersion } from './ActualizarVersion.tsx';
import { marcarNuevaVersion, usarNuevaVersion } from '../../logica/nueva-version.ts';
import { Boton, Cargando, Chip, ErrorEnLinea, Vacio } from '../../componentes/ui.tsx';
import { ETIQUETA_ESTADO, ETIQUETA_OPCION, ETIQUETA_SEMAFORO, colorSemaforo, dinero, etiquetaTipoDe, kwp } from '../../logica/etiquetas.ts';
import { tonoEstado } from '../Cotizaciones.tsx';
import { lecturaConDatos } from '../../logica/lectura.ts';
import { ProveedorCotizacion, usarCotizacion } from './contexto.tsx';
import { SeccionRecibos } from './SeccionRecibos.tsx';
import { SeccionRevision } from './SeccionRevision.tsx';
import { SeccionCostos } from './SeccionCostos.tsx';
import { SeccionOpciones } from './SeccionOpciones.tsx';
import { SeccionPdf } from './SeccionPdf.tsx';
import { SeccionSeguimiento } from './SeccionSeguimiento.tsx';
import { SeccionAmpliacion } from './SeccionAmpliacion.tsx';
import { kwhCorto, textoMsi } from '../../logica/ampliacion.ts';
import { usarPendienteAmpliacion } from '../../logica/pendientes.ts';
import { SugerenciasNotas } from './SugerenciasNotas.tsx';
import { CambiarVendedor, TextoVendedor } from './VendedorCabeza.tsx';
import { SucursalesProyecto } from './SucursalesProyecto.tsx';
import { SepararSitio } from './SepararSitio.tsx';

export const SECCIONES = ['recibos', 'revision', 'costos', 'opciones', 'ampliacion', 'pdf', 'seguimiento'] as const;
export type SeccionId = (typeof SECCIONES)[number];

/** Las secciones de ESTA cotización. Una ampliación de baterías (Diego, 27-oct-2026) no tiene recibos, revisión, costos
 *  adicionales ni opciones: su captura y su precio viven en «Ampliación». Las solares, como siempre (sin «Ampliación»). */
export function seccionesDe(q: { tipo: string }): readonly SeccionId[] {
  return q.tipo === 'ampliacion_baterias' ? ['ampliacion', 'pdf', 'seguimiento'] : ['recibos', 'revision', 'costos', 'opciones', 'pdf', 'seguimiento'];
}

const NOMBRE: Record<SeccionId, string> = {
  ampliacion: 'Ampliación',
  recibos: 'Recibos',
  revision: 'Revisión',
  costos: 'Costos',
  opciones: 'Opciones',
  pdf: 'PDF',
  seguimiento: 'Seguimiento',
};

export function recibosSubidos(c: Cotizacion, medidor: 'A' | 'B'): number {
  return c.borrador.archivos.filter((a) => a.clase === 'recibo' && !a.eliminado && a.medidor === medidor).length;
}

/** Sección que conviene abrir según cómo va la cotización. */
export function seccionInicial(c: Cotizacion, esTecnico: boolean): SeccionId {
  const q = c.borrador.cuestionario;
  if (q.tipo === 'ampliacion_baterias') return 'ampliacion';
  if (esTecnico) return 'costos';
  if (q.consumo.es_nuevo) return 'opciones';
  if (!lecturaConDatos(c.borrador.lecturas.A)) return recibosSubidos(c, 'A') === 0 ? 'recibos' : 'revision';
  if (!c.borrador.resultado) return 'revision';
  return 'opciones';
}

/** Sección donde vive el dato de un aviso (para el botón "Ver el dato"). */
export function seccionDeCampo(campo: string): SeccionId {
  if (campo === '/piezas' || campo.startsWith('/piezas/')) return 'ampliacion';
  if (campo.startsWith('/lecturas')) return 'revision';
  if (campo.startsWith('/costos_adicionales')) return 'costos';
  if (campo.startsWith('/archivos')) return 'recibos';
  if (campo.startsWith('/cuestionario')) return 'seguimiento';
  return 'opciones';
}

/** Lo que enseña el encabezado (y el punto de la pestaña Opciones). */
export type PrecioEncabezado = {
  /** Qué se cobra, después de «Contado con IVA · »: «exacta (8.06 kWp)», «exacta con baterías (8.06 kWp · ~4 h)», «respaldo ~4 h». */
  etiqueta: string;
  /** null = el nivel de baterías recomendado no tiene precio en la tabla (el PDF no sale y el CRM tampoco cuenta monto). */
  contado: number | null;
  lista: number | null;
  precio_unico: boolean;
  usd_por_w: number | null;
  semaforo: Semaforo | null;
  /** Solo Diego (el servidor no manda la rentabilidad a nadie más). */
  margen_pct: number | null;
  /** Lo que se lee cuando no hay contado (por omisión «Sin precio en la tabla»): el recorte de punta que no se ofrece lo dice. */
  sin_precio?: string;
  promo_normal?: number | null;
};

/** Horas del nivel para la etiqueta: las de diseño («~4 h») si el rango que da de verdad las incluye; si no (resultado de
 *  antes, o el vendedor movió las baterías), el tope del rango, como el nombre del banco («Hasta N horas»). */
function horasDelNivel(n: NivelRespaldoCalculado): string {
  const o = n.horas_objetivo;
  return o !== undefined && n.horas.min <= o && o <= n.horas.max ? `~${numero(o)} h` : `hasta ${n.horas.max} h`;
}

/** E4a de BATERÍAS POR OPCIÓN (Diego, 24-oct-2026; DISENO-BATERIAS-POR-OPCION): el encabezado cobra lo MISMO que el PDF y el
 *  CRM. Con baterías (formato de baterías con niveles calculados) es `loQueCobraElPdf`: el nivel recomendado (en el híbrido y
 *  el aislado, paneles + baterías) y SU semáforo, que mide el total (decisión 8); antes enseñaba la opción de paneles sola
 *  (verificación adversarial del 14-oct: $254,200 y verde contra $520,500 en el PDF). Sirve con resultados de antes (un solo
 *  `hibrido`) y por opción (`por_opcion`: el `hibrido` es el sistema del nivel recomendado). Sin baterías, lo de siempre. */
export function precioDelEncabezado(b: Pick<ContenidoCotizacion, 'cuestionario' | 'resultado'>): PrecioEncabezado | null {
  const r = b.resultado;
  if (!r) return null;
  // RECORTE DE PUNTA (z54): el encabezado cobra la BATERÍA, como el PDF, el CRM y la lista (loQueCobraElPdf); las opciones de paneles
  // son de referencia (verificación de hoy70: enseñaba «arranque (906.44 kWp) $14,066,000» encima de la tarjeta de $1,685,100)
  if (formatoPara(b.cuestionario, r.tarifa) === 'horaria') {
    const k = r.recorte_punta ?? null;
    const pdf = loQueCobraElPdf(b);
    return {
      etiqueta: k ? `recorte de punta (${k.equipo.gabinetes} ${k.equipo.gabinetes === 1 ? 'gabinete' : 'gabinetes'} · de ${numero(k.kw_temporada.min)} a ${numero(k.kw_temporada.max)} kW)` : 'recorte de punta',
      contado: pdf.contado,
      lista: pdf.lista,
      precio_unico: true,
      usd_por_w: null,
      semaforo: k?.se_ofrece ? colorSemaforo(k) : null,
      margen_pct: pdf.rentabilidad?.margen_contado_pct ?? null,
      sin_precio: k ? 'No se ofrece: revisa el aviso rojo' : 'Sin calcular: revisa el aviso rojo',
    };
  }
  const res = r.respaldo ?? null;
  if (res && formatoPara(b.cuestionario, r.tarifa) === 'baterias') {
    const pdf = loQueCobraElPdf(b);
    const n = res.niveles.find((x) => x.id === res.recomendado) ?? null;
    const horas = n ? horasDelNivel(n) : null;
    // la opción de paneles que suma es la de `hibrido` (de ahí sale el kWp del CRM); con por_opcion es la del nivel
    const opcion = res.tipo === 'respaldo' ? null : (res.hibrido?.opcion ?? n?.sistema?.opcion ?? null);
    const kwpOpcion = pdf.kwp ?? (opcion ? (r.opciones.find((o) => o.id === opcion)?.kwp ?? null) : null);
    const detalle = [kwpOpcion !== null ? kwp(kwpOpcion) : null, horas].filter((x): x is string => x !== null).join(' · ');
    const etiqueta = opcion
      ? `${ETIQUETA_OPCION[opcion].toLowerCase()} con baterías${detalle ? ` (${detalle})` : ''}`
      : `${res.tipo === 'respaldo' ? 'respaldo' : 'baterías'}${horas ? ` ${horas}` : ''}`;
    return {
      etiqueta,
      contado: pdf.contado,
      lista: pdf.lista,
      precio_unico: false,
      usd_por_w: null,
      semaforo: n?.precio ? colorSemaforo(n) : null,
      margen_pct: pdf.rentabilidad?.margen_contado_pct ?? null,
    };
  }
  const rec = r.opciones.find((o) => o.id === r.recomendada) ?? null;
  if (!rec) return null;
  return {
    etiqueta: `${ETIQUETA_OPCION[rec.id].toLowerCase()} (${kwp(rec.kwp)})`,
    contado: rec.precio.contado,
    promo_normal: rec.precio.promo?.contado_normal ?? null,
    lista: rec.precio.lista,
    precio_unico: rec.precio.precio_unico === true,
    usd_por_w: rec.precio.contado_sin_iva_usd_por_w ?? null,
    semaforo: colorSemaforo(rec),
    margen_pct: rec.rentabilidad?.margen_contado_pct ?? null,
  };
}

/** Ampliación de baterías: lo que cobra su propuesta de piezas (contado y lista con IVA, como el CRM: loQueCobraElPdf). El
 *  margen, solo Diego (el servidor no manda la regla ni el margen a nadie más); el color, quien lo tenga en el registro. */
export function precioAmpliacion(c: Pick<Cotizacion, 'borrador' | 'semaforo_margen'>): PrecioEncabezado | null {
  const p = c.borrador.calculo_piezas ?? null;
  if (c.borrador.cuestionario.tipo !== 'ampliacion_baterias' || !p) return null;
  const m = margenPiezas(c.borrador);
  return {
    etiqueta: `ampliación (de ${kwhCorto(p.kwh_hoy)} a ${kwhCorto(p.kwh_con)} kWh)`,
    contado: p.contado_con_iva,
    lista: p.lista_con_iva,
    precio_unico: false,
    usd_por_w: null,
    semaforo: m?.semaforo ?? c.semaforo_margen ?? null,
    margen_pct: m?.margen_pct ?? null,
  };
}

export function EspacioCotizacion({ id, seccion }: { id: string; seccion: string | null }) {
  return (
    <ProveedorCotizacion
      id={id}
      cargando={
        <div className="pagina">
          <div className="cabeza-cot cabeza-cot--cargando" aria-hidden />
          <Cargando renglones={8} etiqueta="Abriendo la cotización" />
        </div>
      }
      alError={(e, reintentar) => (
        <div className="pagina">
          <Vacio
            titulo="No se pudo abrir la cotización"
            accion={
              <div className="fila-botones">
                <Enlace a="/cotizaciones" className="boton boton--secundario">
                  Volver a la lista
                </Enlace>
                <Boton variante="principal" icono={<RefreshCw aria-hidden />} onClick={reintentar}>
                  Intentar otra vez
                </Boton>
              </div>
            }
          >
            <ErrorEnLinea error={e} />
            {e instanceof ErrorDeApi && e.codigo === 'SIN_PERMISO' && (
              <p className="nota-suave">De las cotizaciones de otros vendedores ves el renglón de la lista (cliente, monto y estado); los recibos, datos y PDF solo los abre su vendedor o Diego.</p>
            )}
          </Vacio>
        </div>
      )}
    >
      <Espacio seccion={seccion} />
    </ProveedorCotizacion>
  );
}

function Espacio({ seccion }: { seccion: string | null }) {
  const { cot, detalle, conflicto, recargar, ejecutar } = usarCotizacion();
  const { usuario, puede, puedeEn } = usarUsuario();
  // Política "vendedor externo edita solo lo suyo" (contrato v2): su rol podría editar, pero esta cotización no es suya.
  // En la papelera no se edita (puedeEn da false) y lo dice su propia franja, no la de «es de otro vendedor».
  const soloVer = !cot.papelera && puede('editar_cotizaciones') && !puedeEn('editar_cotizaciones', cot);
  const visibles = seccionesDe(cot.borrador.cuestionario);
  // Una sección que esta cotización no tiene (los recibos de una ampliación, o «ampliacion» en una solar) lleva a la inicial.
  const actual: SeccionId | null = seccion && (visibles as readonly string[]).includes(seccion) ? (seccion as SeccionId) : null;

  const pista = useRef<HTMLDivElement>(null);
  useEffect(() => {
    // En el celular la barra de secciones se desplaza: la sección abierta siempre queda a la vista.
    const activo = pista.current?.querySelector<HTMLElement>('.es-activo');
    if (activo && typeof activo.scrollIntoView === 'function') activo.scrollIntoView({ block: 'nearest', inline: 'center' });
  }, [actual]);

  useEffect(() => {
    if (!actual) navegar(`/cotizaciones/${cot.id}/${seccionInicial(cot, usuario.rol === 'tecnico')}`, { reemplazar: true });
  }, [actual, cot, usuario.rol]);

  // «Ir a arreglar» (verificador del 2-nov): con la franja abierta, la pestaña quedaba hasta 2,324 px abajo y el vendedor solo veía la misma lista.
  useEffect(() => {
    if (actual && tomarLlegadaALaSeccion()) return mostrarLaSeccion();
    return undefined;
  }, [actual]);

  const b = cot.borrador;
  const esAmpliacion = b.cuestionario.tipo === 'ampliacion_baterias';
  const cab = esAmpliacion ? precioAmpliacion(cot) : precioDelEncabezado(b);
  // Lo tecleado en «Ampliación» y sin guardar (se llegó a otra pestaña con «Atrás»): se dice en cualquier otra pestaña.
  const pendiente = usarPendienteAmpliacion(cot.id, b.piezas ?? null);
  // z48-7: «Hacer cambios (nueva versión)» (pestaña PDF): desde qué versión se está haciendo la siguiente.
  const haciendo = usarNuevaVersion(cot.id, cot.version_actual);
  const editable = puedeEn('editar_cotizaciones', cot);
  const q = b.cuestionario;
  const esperados = recibosEsperados(q, b.lecturas.A?.tarifa ?? null);
  // Etapa 4: ¿hay una tabla leída que el vendedor todavía no autoriza? (no se calcula hasta entonces) ¿De qué medidor?
  const pendientes: Medidor[] = q.consumo.es_nuevo ? [] : (q.dos_medidores ? (['A', 'B'] as const) : (['A'] as const)).filter((m) => lecturaPorAutorizar(b.lecturas[m]));
  const porAutorizar = pendientes.length > 0;
  // El medidor que se ve en Revisión vive aquí: la franja lleva a la pestaña del que falta (verificación adversarial).
  const [medidorRevision, setMedidorRevision] = useState<Medidor>(() => pendientes[0] ?? 'A');
  const [irTabla, setIrTabla] = useState(0);
  const subidosA = recibosSubidos(cot, 'A');
  const subidosB = recibosSubidos(cot, 'B');
  // PAPELERA (Diego, 13-oct): solo Diego la abre (el servidor da 403 a los demás) y la recupera desde aquí o desde la lista.
  const [recuperando, setRecuperando] = useState(false);
  const [errorRecuperar, setErrorRecuperar] = useState<unknown>(null);
  const recuperar = async () => {
    setRecuperando(true);
    setErrorRecuperar(null);
    try {
      await ejecutar((c) => api.recuperarCotizacion(c.id, c.revision));
    } catch (e) {
      setErrorRecuperar(e);
    } finally {
      setRecuperando(false);
    }
  };

  const resolver = async (a: Aviso, nota: string) => {
    await ejecutar((c) => api.resolverAviso(c.id, a.id, c.revision, nota));
  };
  // Hallazgo del 10-oct: "Ver el dato" de un aviso del cuestionario (la tarifa de un negocio nuevo…) llevaba a
  // Seguimiento; si la persona puede editarlo, abre el cuestionario.
  // Ampliación de baterías: «Ver el dato» abre «Ampliación» con ese campo enfocado (?campo=/piezas/…).
  const irA = (campo: string) =>
    navegar(
      campo.startsWith('/cuestionario') && puedeEn('editar_cotizaciones', cot)
        ? // ADENDA z52-2: el aumento de consumo pendiente abre el cuestionario en «Aumento de consumo»
          `/cotizaciones/${cot.id}/cuestionario/editar${campo === '/cuestionario/consumo' ? '?campo=aumento' : ''}`
        : seccionDeCampo(campo) === 'ampliacion'
          ? `/cotizaciones/${cot.id}/ampliacion?campo=${encodeURIComponent(campo)}`
          : `/cotizaciones/${cot.id}/${seccionDeCampo(campo)}`,
    );

  const insignia = (s: SeccionId): string | null => {
    if (s === 'recibos') {
      if (q.consumo.es_nuevo) return b.archivos.filter((a) => !a.eliminado).length ? String(b.archivos.filter((a) => !a.eliminado).length) : null;
      const total = q.dos_medidores ? subidosA + subidosB : subidosA;
      return `${total}/${esperados * (q.dos_medidores ? 2 : 1)}`;
    }
    if (s === 'revision') return porAutorizar ? 'Autorizar' : null;
    if (s === 'costos') return b.costos_adicionales.length ? String(b.costos_adicionales.length) : null;
    if (s === 'pdf') return cot.version_actual ? `v${cot.version_actual}${pdfDesactualizado(detalle) ? ' · viejo' : ''}` : null;
    return null;
  };

  return (
    <div className="espacio">
      <header className="cabeza-cot">
        <div className="cabeza-cot__interior">
          <div className="cabeza-cot__fila">
            <Enlace a="/cotizaciones" className="cabeza-cot__volver">
              <ArrowLeft aria-hidden />
              <span>Cotizaciones</span>
            </Enlace>
            <span className="folio folio--claro">{cot.folio}</span>
            <Chip tono={tonoEstado(cot.estado)}>{ETIQUETA_ESTADO[cot.estado]}</Chip>
          </div>
          <div className="cabeza-cot__cuerpo">
            <div className="cabeza-cot__cliente">
              <h1 className="cabeza-cot__nombre">{cot.cliente_nombre}</h1>
              <p className="cabeza-cot__meta">
                {etiquetaTipoDe(q)}
                {q.cliente.giro ? ` · ${q.cliente.giro}` : ''} · {cot.ciudad} · {cot.etapa === 'final' ? 'Cotización final' : 'Precotización'}
                {cot.tarifa ? ` · ${cot.tarifa}` : ''}
                <TextoVendedor />
              </p>
              <CambiarVendedor />
              {/* z48 (Diego, 28-oct): «ya no deja regresarte al cuestionario»: estaba solo en Seguimiento. Aquí, siempre a la vista. */}
              {editable && (
                <Enlace a={`/cotizaciones/${cot.id}/cuestionario/editar`} className="boton boton--secundario boton--chico">
                  <Pencil aria-hidden />
                  <span>Editar cuestionario</span>
                </Enlace>
              )}
            </div>
            <div className="cabeza-cot__precio" aria-live="polite">
              {cab ? (
                <>
                  <p className="cabeza-cot__etiqueta">Contado con IVA · {cab.etiqueta}</p>
                  {cab.contado !== null ? (
                    <>
                      <p className="cabeza-cot__cifra">{dinero(cab.contado)}</p>
                      {cab.promo_normal != null && b.parametros.promo && <p className="cabeza-cot__lista" data-promo>Promo {promoAlDia(b.parametros.promo, hoyEnHermosillo()).nombre} · normal {dinero(cab.promo_normal)}</p>}
                    </>
                  ) : (
                    // el nivel de baterías recomendado sin precio en la tabla: no se enseñan los paneles solos (el PDF no sale)
                    <p className="cabeza-cot__sin">{cab.sin_precio ?? 'Sin precio en la tabla'}</p>
                  )}
                  {cab.contado !== null && (
                    <p className="cabeza-cot__lista">
                      {/* Precio único (Diego, 13-oct): comercio o industria de más de $1 M, sin lista ni descuento de contado */}
                      {/* Ampliación: los 3 MSI del cálculo (msi_3; arriba de $500,000 de lista no hay: el PDF no los imprime) */}
                      {cab.precio_unico ? 'Precio único, sin lista' : esAmpliacion && b.calculo_piezas ? textoMsi(b.calculo_piezas) : `Con tarjeta: ${dinero(cab.lista)}`}
                      {cab.usd_por_w !== null && <span className="cabeza-cot__porw"> · {numero(cab.usd_por_w, 3)} USD/W</span>}
                      {/* Margen %: solo Diego (ADENDA A0b, 11-oct). El color: Diego y los asesores internos (11-oct). Lo decide el servidor. */}
                      {cab.semaforo && (
                        <span className={`semaforo semaforo--${cab.semaforo}`} data-semaforo={cab.semaforo}>
                          <span className="semaforo__punto" aria-hidden />
                          {ETIQUETA_SEMAFORO[cab.semaforo]}
                          {cab.margen_pct !== null ? ` · ${cab.margen_pct.toLocaleString('es-MX', { maximumFractionDigits: 1 })} %` : ''}
                        </span>
                      )}
                    </p>
                  )}
                </>
              ) : (
                <>
                  <p className="cabeza-cot__etiqueta">Sin precio todavía</p>
                  <p className="cabeza-cot__sin">{porAutorizar ? 'Falta autorizar lo leído' : esAmpliacion ? 'Faltan datos de la ampliación' : 'Faltan datos para calcular'}</p>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {conflicto && (
        <div className="franja-conflicto" role="alert">
          <OctagonAlert aria-hidden />
          {/* Verificación adversarial de 9e40380: decía «Alguien más guardó cambios… (lo que no guardaste se pierde)» también
              cuando el cambio era de la misma persona en otra pestaña. Quién, qué y cuándo lo dice el error junto al botón
              (el servidor lo explica); aquí, solo lo que siempre es cierto. */}
          <span>Esta cotización cambió desde que la abriste (en otra pestaña, en otro teléfono o por otra persona). Recárgala para seguir sobre lo último.</span>
          <Boton chico variante="noche" icono={<RefreshCw aria-hidden />} onClick={() => void recargar()}>
            Recargar
          </Boton>
        </div>
      )}

      {cot.papelera && (
        <div className="franja-solo-ver" role="note" aria-label="En la papelera">
          <Trash2 aria-hidden />
          <span>
            {MENSAJE_EN_PAPELERA} La mandó <strong>{cot.papelera.nombre}</strong> el {fechaHoraCorta(cot.papelera.en)}.
          </span>
          {puede('administrar_papelera') && (
            <Boton chico variante="secundario" icono={<RotateCcw aria-hidden />} cargando={recuperando} onClick={() => void recuperar()}>
              Recuperar
            </Boton>
          )}
        </div>
      )}
      {cot.papelera && <ErrorEnLinea error={errorRecuperar} alCerrar={() => setErrorRecuperar(null)} />}

      {soloVer && (
        <div className="franja-solo-ver" role="note">
          <Lock aria-hidden />
          <span>
            Esta cotización es de <strong>{detalle.vendedor_nombre}</strong>: la puedes ver, pero solo la edita su vendedor o Diego.
          </span>
        </div>
      )}

      <BandaActualizarVersion />
      <BandaRojoDeOtraVersion />

      {haciendo !== null && editable && (
        <div className="aviso-ancho aviso-ancho--info" role="note" aria-label="Haciendo una versión nueva">
          <p>
            <strong>
              Haciendo la versión {haciendo + 1} de {cot.folio}.
            </strong>{' '}
            Cambia lo que necesites (opciones, costos o el cuestionario): lo capturado se queda. Al terminar genera la versión {haciendo + 1}; la {haciendo} queda en «Versiones anteriores».
          </p>
          <div className="fila-botones">
            <Enlace a={`/cotizaciones/${cot.id}/cuestionario/editar`} className="boton boton--secundario boton--chico">
              <Pencil aria-hidden />
              <span>Editar el cuestionario</span>
            </Enlace>
            <Boton chico variante="principal" icono={<FileDown aria-hidden />} onClick={() => navegar(`/cotizaciones/${cot.id}/pdf`)}>
              Generar la versión {haciendo + 1}
            </Boton>
            <Boton chico variante="fantasma" onClick={() => marcarNuevaVersion(cot.id, null)}>
              Ya no
            </Boton>
          </div>
        </div>
      )}

      {cot.multisitio && <SucursalesProyecto cot={cot} editable={!cot.papelera && puedeEn('editar_cotizaciones', cot)} />}

      <nav className="secciones" aria-label="Secciones de la cotización">
        <div className="secciones__pista" ref={pista}>
          {visibles.map((s) => {
            const ins = insignia(s);
            const rojoAqui = b.avisos.some((a) => a.nivel === 'rojo' && !a.resuelto && a.campo && seccionDeCampo(a.campo) === s);
            return (
              <Enlace
                key={s}
                a={`/cotizaciones/${cot.id}/${s}`}
                className={['secciones__item', actual === s ? 'es-activo' : ''].join(' ')}
                aria-current={actual === s ? 'page' : undefined}
              >
                {s === 'recibos' && q.consumo.es_nuevo ? 'Archivos' : NOMBRE[s]}
                {ins && <span className="secciones__insignia">{ins}</span>}
                {(s === 'opciones' || s === 'ampliacion') && cab?.semaforo && <span className={`semaforo__punto semaforo__punto--${cab.semaforo}`} aria-label={ETIQUETA_SEMAFORO[cab.semaforo]} />}
                {rojoAqui && <span className="secciones__rojo" aria-label="tiene aviso rojo" />}
              </Enlace>
            );
          })}
        </div>
      </nav>

      <div className="espacio__cuerpo">
        {/* Hallazgo del 10-oct: en Recibos, el subidor va PRIMERO (los botones de subir quedaban abajo del pliegue,
            debajo de los avisos); ahí los avisos y las sugerencias van después. */}
        {actual === 'recibos' && <SepararSitio cot={cot} editable={!cot.papelera && puedeEn('editar_cotizaciones', cot)} />}
        {actual === 'recibos' && <SeccionRecibos />}
        {/* Etapa 4: en el celular la tabla queda abajo de las notas y los avisos; lo primero que se ve dice qué falta y
            lleva a la tabla (ahí se revisa, se corrige y se autoriza). */}
        {actual === 'revision' && porAutorizar && (
          <div className="aviso-ancho aviso-ancho--ambar franja-autorizar" role="note" aria-label="Falta autorizar la tabla">
            <p>
              <strong>{q.dos_medidores ? `Falta autorizar la tabla del medidor ${pendientes.join(' y la del ')}.` : 'Falta autorizar la tabla que se leyó.'}</strong> Revísala contra el recibo, corrígela si hace falta y
              autorízala al final de la tabla: las opciones se calculan al autorizarla.
            </p>
            <Boton
              chico
              icono={<ArrowDown aria-hidden />}
              onClick={() => {
                if (!pendientes.includes(medidorRevision)) setMedidorRevision(pendientes[0]!);
                setIrTabla((n) => n + 1);
              }}
            >
              Ir a la tabla
            </Boton>
          </div>
        )}
        {/* «Lo que entendí de tus notas» propone cambios del cuestionario SOLAR (dos medidores, ampliación de paneles…): en una
            ampliación de baterías no aplica (su cuestionario no los admite). */}
        {actual !== 'pdf' && actual !== 'seguimiento' && !esAmpliacion && <SugerenciasNotas />}
        {actual !== 'pdf' && (
          <BandaAvisos
            avisos={b.avisos}
            alResolver={puedeEn('editar_cotizaciones', cot) ? resolver : undefined}
            alIr={irA}
            // z48-5: arriba solo lo que detiene el PDF; para quien no es Diego, también el precio que solo él mantiene (Optimus).
            detiene={(a) => detieneElPdf(a) || (!a.resuelto && a.nivel === 'rojo' && !puede('aprobar_precios') && CODIGOS_QUE_SOLO_CONFIRMA_DIEGO.includes(a.codigo))}
            // Los rojos que no se confirman (recibo quitado, doble estructura, el de la ley) no se «ven y olvidan» desde aquí: se
            // arregla el dato; el PDF sale igual y Diego los recibe en su bitácora (verificación adversarial del 13-oct, noche).
            comoSeResuelve={(a) => (CODIGOS_QUE_NO_SE_CONFIRMAN.includes(a.codigo) || (CODIGOS_QUE_SOLO_CONFIRMA_DIEGO.includes(a.codigo) && !puede('aprobar_precios')) ? 'arreglar' : CODIGOS_QUE_APRUEBA_DIEGO.includes(a.codigo) && !puede('aprobar_precios') ? 'ok_diego' : 'nota')}
          />
        )}
        {esAmpliacion && pendiente && actual !== 'ampliacion' && actual !== null && (
          <div className="aviso-ancho aviso-ancho--ambar" role="note" aria-label="Cambios sin guardar en Ampliación">
            <p>
              <strong>{pendiente.cambios === 1 ? 'Tienes 1 cambio sin guardar' : pendiente.cambios > 1 ? `Tienes ${pendiente.cambios} cambios sin guardar` : 'Tienes cambios sin guardar'} en «Ampliación».</strong> El
              precio y el PDF son los de lo guardado. <Enlace a={`/cotizaciones/${cot.id}/ampliacion`}>Ir a guardarlos</Enlace>
            </p>
          </div>
        )}
        {actual === 'revision' && <SeccionRevision medidor={medidorRevision} alCambiarMedidor={setMedidorRevision} irTabla={irTabla} />}
        {actual === 'costos' && <SeccionCostos />}
        {actual === 'opciones' && <SeccionOpciones />}
        {actual === 'ampliacion' && <SeccionAmpliacion />}
        {actual === 'pdf' && <SeccionPdf />}
        {actual === 'seguimiento' && <SeccionSeguimiento />}
        {!actual && <Cargando />}
        {subidosA > RECIBOS_MAXIMOS_POR_MEDIDOR && (
          <p className="nota-suave">Hay más de {RECIBOS_MAXIMOS_POR_MEDIDOR} recibos en el medidor A: quita los repetidos.</p>
        )}
      </div>
    </div>
  );
}
